/**
 * 路線形状 GeoJSON のフェッチ。
 *
 * - 線形データは「方面」(routeDirection) ごとに 1 ファイルとして
 *   `web-app/public/data/route_shapes/<route_id>_<direction_name>.geojson`
 *   に同梱されている。
 * - 各 Feature は GPS 観測点をサンプルした **Point** ジオメトリで、
 *   properties に GPS 精度 (`gps_accuracy_avg/min/max`) や
 *   アナウンス有無 (`is_announcing`) などの計測値を持つ。
 * - ファイルが存在しない (= 旧デプロイや未対応路線) 場合は `null` を返し、
 *   呼び出し側で「線形データを表示しない」仕様とする。
 */

/**
 * route_shapes/*.geojson の各 Feature の properties。
 *
 * すべての値は欠損 (null) する可能性がある (まだ計測点が取れていない時)。
 */
export interface RouteShapePointProperties {
  route?: string;
  direction?: string;
  from_station?: string;
  to_station?: string;
  sequence?: number;
  /** GPS 精度 (m) の平均。値が小さいほど高精度。 */
  gps_accuracy_avg: number | null;
  gps_accuracy_min: number | null;
  gps_accuracy_max: number | null;
  /** GPS マッチした計測サンプル数 */
  gps_match_count: number | null;
  /**
   * 当該地点でアナウンスが行われた実績。
   * - `true`: アナウンス区間 (赤系で表示)
   * - `false`: 非アナウンス区間 (灰系で表示)
   * - `null`: 未取得 (薄灰で表示)
   */
  is_announcing: boolean | null;
  recorded_at: string | null;
}

export interface RouteShapePointFeature {
  type: 'Feature';
  properties: RouteShapePointProperties;
  geometry: {
    type: 'Point';
    /** [lng, lat] (GeoJSON 規約) */
    coordinates: [number, number];
  };
}

/** 方面ごとの Point Feature コレクション */
export interface RouteShapePointsFile {
  type: 'FeatureCollection';
  name?: string;
  features: RouteShapePointFeature[];
}

/**
 * 指定した route_id / direction_name に対応する線形 GeoJSON を取得する。
 *
 * - 404 / JSON パースエラーなど取得不可の場合は `null` を返す。
 *   呼び出し側 (地図) は `null` の時に線形を描画しないこと。
 *
 * @param routeId        例: "中総緩"
 * @param directionName  例: "千葉方面（B線）"
 */
export async function fetchRouteShapePoints(
  routeId: string,
  directionName: string,
): Promise<RouteShapePointsFile | null> {
  if (!routeId || !directionName) return null;

  // ファイル名にマルチバイト文字 (例: "中総緩_千葉方面（B線）.geojson") が含まれるため、
  // URL に渡す際は encodeURIComponent でエンコードする。
  const filename = encodeURIComponent(`${routeId}_${directionName}.geojson`);
  const url = `/data/route_shapes/${filename}`;

  let res: Response;
  try {
    res = await fetch(url, { cache: 'no-store' });
  } catch {
    return null;
  }
  if (!res.ok) return null;

  try {
    const json = (await res.json()) as RouteShapePointsFile;
    if (!json || json.type !== 'FeatureCollection' || !Array.isArray(json.features)) {
      return null;
    }
    return json;
  } catch {
    return null;
  }
}
